import React, { useEffect, useState, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import useApi from '../../../utils/AxiosWrapper';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../utils/UserContext';
import { setApplicationStep } from '../../../pages/signup';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
} from '../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { Application } from '../../../types/dbTypes';
import useFrontChat from '../../../hooks/useFrontChat';
interface PlaidLinkProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application_id: string;
  application: Application;
}

function PlaidLinkRetry({
  currStep,
  selectedRole,
  setStep,
  application_id,
  application,
}: PlaidLinkProps) {
  // Initialize Front chat widget
  useFrontChat();

  const { callProtectedApi } = useApi();
  const navigate = useNavigate();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [openLink, setOpenLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userRecord, userId } = useUser();
  const { screenView } = useScreenView();

  useEffect(() => {
    const fetchLinkToken = async (customer_id: string) => {
      setLoading(true);
      try {
        const body = {
          owner_id: customer_id,
          entity: 'customer', // Get curr (Customer, Vendor, or Partner)
        };
        const response = await callProtectedApi(
          '/linkedAccounts/plaidLinkToken',
          'POST',
          body,
          'Error occurred while connecting to Plaid.',
          {},
          undefined,
          5
        );
        if (response?.data?.data?.token) {
          setLinkToken(response.data.data.token);
        }
      } catch (error: any) {
        console.error('Error fetching Plaid link token:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLinkToken(
      userRecord?.userType === Entity.Customer ? userRecord?.customer_id : ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  const onSuccess = useCallback(
    async (public_token: string) => {
      try {
        const step = await setApplicationStep({
          step: ApplicationStep.PendingReview,
          application_id,
          customer_id: userId ?? '',
          callProtectedApi,
          setLoading: handleSetLoading,
          previousStep: ApplicationStep.PlaidLinkRetry,
          applicationStatus: application?.status as ApplicationStatus,
        });
        if (step?.length > 0 && step === ApplicationStep.PendingReview) {
          setStep(ApplicationStep.PendingReview);
        }
        return;
      } catch (error: any) {
        console.error('Error adding payment method:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callProtectedApi, navigate]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess,
  });

  useEffect(() => {
    if (openLink && ready) {
      open();
      setOpenLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLink, ready, open]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {!loading ? (
              <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-10">
                <div className="flex w-full items-center justify-center px-6 pt-[5rem] lg:px-12">
                  <div
                    className={`flex w-[90%] flex-col items-center lg:w-[60%]`}
                  >
                    <div className="flex flex-col items-center">
                      <h2 className="mb-2 justify-center text-center text-[32px] font-semibold text-primary">
                        Link Your Business Bank Account(s)
                      </h2>
                      <div className="flex w-[60%] flex-row items-center space-x-1 align-middle text-[20px] text-[#7D8191]">
                        {/* <Info size={20} className="w-[8%]" /> */}
                        <p className="text-center">
                          Please connect the account where you receive{' '}
                          <b className="underline">direct deposit</b> payments
                          from your factoring company, 3PL, or shippers. This
                          may accelerate your application decision.
                        </p>
                      </div>
                    </div>
                    <div className="my-[5%] flex w-full flex-col items-center align-middle">
                      <button
                        onClick={() => setOpenLink(true)}
                        disabled={!linkToken}
                        className="w-[400px] justify-center rounded-md border border-transparent bg-black px-4 py-2 text-sm font-medium text-white hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 enabled:cursor-pointer disabled:opacity-30"
                      >
                        <div className="flex flex-row items-center space-x-2 align-middle">
                          <img
                            alt="plaid-logo"
                            src="/plaid.svg"
                            className="h-20 w-20 text-white"
                          />{' '}
                          <p className="text-3xl font-bold text-white">
                            Link Bank Account
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PlaidLinkRetry;
